import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Loader2, Save } from "lucide-react";

export const CUSTOM_PAY = "Pagamento personalizzato";

const STEPS = [
  ["acconto", "Acconto alla firma"],
  ["consegna", "Consegna dei materiali"],
  ["dico", "Dichiarazione di conformità (DiCo)"],
  ["allaccio", "Allaccio alla rete"],
  ["dilazionato", "Dilazionato a X giorni dalla DiCo"],
];

const eur = (v) => `€ ${new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" }).format(v || 0)}`;

// Piano di pagamento personalizzato (pratiche rete): voci da flaggare con %, la somma deve fare 100%.
export function CustomPaymentEditor({ value, totale, onSave, disabled }) {
  const [on, setOn] = useState({});
  const [pct, setPct] = useState({});
  const [giorni, setGiorni] = useState(30);
  const [detr, setDetr] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const st = value?.steps || {};
    setOn(Object.fromEntries(STEPS.map(([k]) => [k, !!st[k]])));
    setPct(Object.fromEntries(STEPS.map(([k]) => [k, st[k] ?? ""])));
    setGiorni(value?.giorni || 30);
    setDetr(!!value?.detrazione);
  }, [value]);

  const sum = STEPS.reduce((a, [k]) => a + (on[k] ? Number(pct[k]) || 0 : 0), 0);
  const ok = Math.abs(sum - 100) < 0.01 && (!on.dilazionato || (giorni >= 1 && giorni <= 365));
  const save = async () => {
    setBusy(true);
    const steps = Object.fromEntries(STEPS.filter(([k]) => on[k]).map(([k]) => [k, Number(pct[k]) || 0]));
    try { await onSave({ steps, giorni: Number(giorni) || 30, detrazione: detr }); } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 rounded border border-border p-3 space-y-2" data-testid="custom-pay-editor">
      {STEPS.map(([k, label]) => (
        <div key={k} className="flex flex-wrap items-center gap-2 text-xs">
          <Checkbox checked={!!on[k]} disabled={disabled} onCheckedChange={(v) => setOn((o) => ({ ...o, [k]: !!v }))} data-testid={`custom-pay-on-${k}`} />
          <span className="w-56">{label}</span>
          <Input type="number" min="0" max="100" value={pct[k]} disabled={disabled || !on[k]} onChange={(e) => setPct((p) => ({ ...p, [k]: e.target.value }))} className="h-7 w-20 text-xs" data-testid={`custom-pay-pct-${k}`} />
          <span className="text-muted-foreground">%</span>
          {on[k] && <span className="text-muted-foreground tabular-nums w-24" data-testid={`custom-pay-eur-${k}`}>{eur((totale * (Number(pct[k]) || 0)) / 100)}</span>}
          {k === "dilazionato" && on[k] && (
            <>
              <Input type="number" min="1" max="365" value={giorni} disabled={disabled} onChange={(e) => setGiorni(e.target.value)} className="h-7 w-20 text-xs" data-testid="custom-pay-giorni" />
              <span className="text-muted-foreground">giorni dalla DiCo</span>
            </>
          )}
        </div>
      ))}
      <label className="flex items-center gap-2 text-xs pt-1">
        <Checkbox checked={detr} disabled={disabled} onCheckedChange={(v) => setDetr(!!v)} data-testid="custom-pay-detrazione" />
        Pagamenti per detrazione fiscale (bonifico parlante)
      </label>
      <div className="flex items-center gap-3 pt-1">
        <span className={`text-xs font-semibold ${ok ? "text-primary" : "text-destructive"}`} data-testid="custom-pay-sum">Totale {sum.toLocaleString("it-IT")}% {ok ? "" : "· deve essere 100%"}</span>
        <Button size="sm" onClick={save} disabled={!ok || busy || disabled} data-testid="custom-pay-save">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Salva piano
        </Button>
      </div>
    </div>
  );
}
